<template>
    <div :class="[ 'kaseya-send-widget' ]">
        <div class="widget-header">
            <h3>Send To Kaseya</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isInitializing">
            <loading-screen-svg />
        </div>
        <div v-else class="content-wrapper">
            <kaseya-send-contact v-if="contact" @selectCompany="selectCompany" @removeCompany="removeCompany" :contact="contact" :company="company" />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :onClick="closeWidget">
                     Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button :onClick="sendContactToKaseya" :loading="isSendingContactToKaseya" :disabled="isSendingContactToKaseya || deactivatedSwitches">
                    Send to Kaseya
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const KaseyaSendContact = () => import('@/components/integration/kaseya-send-contact');

import EventBus from '@/scripts/event-bus.js';
import log, { LogLevel } from "@/scripts/logger";

export default {
    name: 'kaseya-send-contact-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'kaseya-send-contact': KaseyaSendContact
    },

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            contact: {
                id: null,
                name: null,
                profileImageUrl: null,
                shouldSendInformation: true,
                shouldSendOpportunities: false,
                shouldSendActivities: false,
                errorMessage: null,
                isUpsert: null,
                clientId: null,
                kaseyaContactId: null,
            },

            company: {
                id: null,
                name: null,
                imageUrl: null,
                kaseyaAccountId: -1,
            },

            isSavingCompanyToContact: false,
            isSendingContactToKaseya: false,
            deactivatedSwitches: false,

            kaseyaExtraInfo: {
                serverUrl: null,
                contactId: null
            },
            
            isInitializing: true
        }
    },

    watch: {
        'contact.shouldSendInformation': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendOpportunities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        },

        'contact.shouldSendActivities': function() {
            this.deactivatedSwitches = !(this.contact.shouldSendInformation
                || this.contact.shouldSendOpportunities
                || this.contact.shouldSendActivities)
        }
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getContact()
            .then(this.getIntegrationInformation)
            .catch((error) => {
                this.$helpers.showFailNotification(error, { error });
            });
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.widget.options.id }&$expand=Company`)
                    .then(response => {
                        this.createContact(response.data.value[0]);
                        if (response.data.value[0].Company) {
                            this.createCompany(response.data.value[0].Company);
                        }
    
                        resolve();
                    })
                    .catch(() => {
                        reject('An error occurred while fetching the contacts you selected.');
                });
            });
        },

        getIntegrationInformation() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/ms/kaseya-api/v1/credentials/${ this.contact.clientId }`)
                    .then(response => {
                        if(response.data) {
                        this.kaseyaExtraInfo.serverUrl = response.data.data.ServerUrl;
                    }
                    })
                    .catch(() => {
                        reject('An error occurred while fetching your Kaseya integration info.');
                    })
                    .finally(() => {
                            this.isInitializing = false;
                    });
            });
        },

        createContact(contactsResponse) {
            this.contact.id = contactsResponse.Id;
            this.contact.name = this.$helpers.getName(contactsResponse.FirstName, contactsResponse.LastName);
            this.contact.profileImageUrl = contactsResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
            this.contact.shouldSendInformation = true;
            this.contact.shouldSendOpportunities = false;
            this.contact.shouldSendActivities = false;
            this.contact.errorMessage = null;
            this.contact.selectedOption = contactsResponse.IsUpsert ? 'upsert' : 'insert';
            this.contact.clientId = contactsResponse.ClientId
            this.contact.kaseyaContactId = contactsResponse.kaseyaContactId;
        },

        createCompany(company) {
            this.company.id = company.Id;
            this.company.name = company.Name;
            this.company.imageUrl = company.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.kaseyaAccountId = company.KaseyaAccountId;
        },

        selectCompany(company) {
            this.company = {
                id: company.id,
                name: company.name,
                imageUrl: company.imageUrl
            }

            this.saveCompanyToContact();
        },

        removeCompany() {
            this.company = null;
        },


        saveCompanyToContact() {
            this.isSavingCompanyToContact = true;

            var changes = [{
                'name': 'CompanyId',
                'value': this.company.id
            }];

            this.$api
                .patch(`/api/Contacts/${ this.widget.options.id }`, changes)
                .then(() => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showSuccessNotification(`<strong>${ this.contact.name }</strong> has been added to ${ this.company.name }`);
                })
                .catch((error) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showFailNotification(`Unable to add ${this.contact.name} to ${this.company.name}.`,
                        { level: LogLevel.DEBUG }
                    );
                    log.error('Unable to add contact to company', error);
                });
        },

        sendContactToKaseya() {
            this.isSendingContactToKaseya = true;
            this.sendCompanyInfo()
            .then(this.sendContactInfo)
            .then(this.getKaseyaContactId)
            .then(() => {
                this.isSendingContactToKaseya = false;
                this.contact.errorMessage = null;

                this.createSuccessMessage();

                EventBus.$emit('contact:update');
                this.closeWidget();
            })
            .catch(error => {
                this.isSendingContactToKaseya = false;
                this.contact.errorMessage = error.Message;
            });
        },

        getKaseyaContactId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts?$filter=Id eq ${ this.contact.id } &$select=KaseyaContactId`)
                    .then(response => {
                        this.kaseyaExtraInfo.contactId = response.data.value[0].KaseyaContactId;
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                });
            });
        },

        createSuccessMessage() {
            var actionTaken = this.contact.isUpsert ? 'updated' : 'inserted';
            var message = `<strong>${ this.contact.name }</strong> has been ${ actionTaken } in Kaseya.`;
            var kaseyaContactLink = `${ this.kaseyaExtraInfo.serverUrl }/CRM/ContactEdit.aspx?ID=${this.kaseyaExtraInfo.contactId}`;
            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: kaseyaContactLink,
                action: null
            });
        },

        sendCompanyInfo() {
            return new Promise((resolve, reject) => {
                if(this.company && !this.company.kaseyaAccountId) {
                    var request = {
                       isUpsert: false 
                    };

                    this.$api
                        .post(`/ms/kaseya-api/v1/integrations/export/companies/${ this.company.id }/${ this.contact.clientId }`, request)
                        .then(() => {
                            this.$helpers.showSuccessNotification(`${ this.company.name } has been inserted in Kaseya`)
                            resolve();
                        })
                        .catch((response) => {
                            reject(response.response.data);
                        });
                }
                else {
                    resolve();
                }
            });
        },
        
        sendContactInfo() {
            return new Promise((resolve, reject) => {
                var request = {
                    isUpsert: this.contact.isUpsert,
                    exportContacts: this.contact.shouldSendInformation,
                    exportDeals: this.contact.shouldSendOpportunities,
                    exportActivities: this.contact.shouldSendActivities
                };

                this.$api
                    .post(`/ms/kaseya-api/v1/integrations/export/contacts/${ this.contact.id }/${ this.contact.clientId }`, request)
                    .then(() => {
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                });
            })
        }
    },

    created() {
        this.getData();
    }
}
</script>

<style scoped>
.kaseya-send-widget .content-wrapper {
    padding-left: 0px;
    padding-right: 0px;
}

.kaseya-send-widget .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.kaseya-send-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18AEFF;
}

.kaseya-send-widget .action-container .btn.transparent-btn:hover {
    background: #DBEDFA;
}

.kaseya-send-widget .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.kaseya-send-widget .company-options-container h4 {
    margin-bottom: 20px;
}

.kaseya-send-widget .widget-content .processing {
    display: block;
    width: 200px;
    margin: 0 auto;
}

.kaseya-send-widget .widget-content .row {
    margin-bottom: 30px;
}
</style>